import {
    type ComponentProps,
    type CSSProperties,
    forwardRef,
    type FunctionComponent,
    useCallback,
    type MouseEvent,
} from 'react';

import { useInitialElementDimensions } from '../../hooks/use-element-dimensions';
import { useImageCDN } from '../../hooks/use-image-cdn';
import { createClassName, createStyleHelper } from '../../utils/class-names';
import { exists } from '../../utils/filters';
import { ContextLink } from '../context-link/context-link';

import styles from './thumbnail.module.scss';

const style = createStyleHelper(styles, 'thumbnail');

export interface ITags {
    sort: number;
    variant: string;
    name: string;
}

export interface IThumbnailProps {
    slug: string;
    background: string;
    title?: string;
    className?: string;
    style?: CSSProperties;
    tags?: ITags[];
    logo?: string | null;
    asset?: string | null;
    variant?: string | null;
    lazy?: boolean;
    compact?: boolean;
    onClick?: (slug: string) => void;
}

type ThumbnailComponent = ComponentProps<FunctionComponent<IThumbnailProps>>;

export const Thumbnail = forwardRef<HTMLAnchorElement, ThumbnailComponent>(
    (
        {
            className,
            slug,
            style: cssStyle,
            title,
            tags,
            background,
            logo,
            asset,
            variant,
            compact,
            onClick,
        },
        ref,
    ) => {
        const { measurementRef, width } = useInitialElementDimensions();
        const assetAndLogoStyles = variant ? variant.toLowerCase() : null;
        const handleClick = useCallback(
            (event: MouseEvent) => {
                if (!onClick) {
                    return;
                }

                event.preventDefault();
                onClick(slug);
            },
            [slug, onClick],
        );
        const backgroundCdnUrl = useImageCDN(background, width);
        const assetCdnUrl = useImageCDN(asset, width);
        const logoCdnUrl = useImageCDN(logo, width);
        const onlyOneImageDefined =
            [backgroundCdnUrl, assetCdnUrl, logoCdnUrl].reduce(
                (acc, image) => (image ? acc + 1 : acc),
                0,
            ) === 1;
        const variants = [onlyOneImageDefined ? 'single' : null, assetAndLogoStyles].filter(exists);

        return (
            <ContextLink
                ref={ref}
                onClick={handleClick}
                to={`/gameplay/${slug}`}
                style={cssStyle}
                className={createClassName([className, style()])}
            >
                {tags ? (
                    <ul className={style('tags')}>
                        {tags.map((tag) => (
                            <li
                                key={tag?.name}
                                className={style('tag', [tag.variant.toLowerCase()])}
                            >
                                {tag.name.toUpperCase()}
                            </li>
                        ))}
                    </ul>
                ) : null}
                <div ref={measurementRef} className={style('layout', variants)}>
                    {backgroundCdnUrl ? (
                        <img
                            className={style('background-image', variants)}
                            src={backgroundCdnUrl}
                        />
                    ) : null}

                    {assetCdnUrl ? (
                        <img className={style('asset', variants)} src={assetCdnUrl} />
                    ) : null}

                    {logoCdnUrl ? (
                        <img className={style('logo', variants)} src={logoCdnUrl} />
                    ) : null}
                </div>

                {title ? (
                    <div className={style('title', { compact: !!compact })}>
                        <p className={style('text')}>{title}</p>
                    </div>
                ) : null}
            </ContextLink>
        );
    },
);
